/**
 * UoM Conversion Utility
 * Shared across PO, Quotation, OC, Production for unit-aware rate conversion.
 *
 * Conversion definition: { from_unit: "MT", to_unit: "KG", factor: 1000 }
 * Means: 1 MT = 1000 KG
 */

/**
 * Get all allowed UoMs for a product (base + alternates from conversions)
 */
export function getAllowedUOMs(product) {
  if (!product) return [];
  const uoms = new Set();
  if (product.unit) uoms.add(product.unit);
  if (product.buy_unit) uoms.add(product.buy_unit);
  if (product.sell_unit) uoms.add(product.sell_unit);
  if (product.use_unit) uoms.add(product.use_unit);
  const conversions = product.unit_conversions || product.conversions || [];
  conversions.forEach(c => {
    if (c.from_unit) uoms.add(c.from_unit);
    if (c.to_unit) uoms.add(c.to_unit);
  });
  return [...uoms].filter(Boolean);
}

/**
 * Convert rate from one unit to another using product's conversion rules.
 *
 * Example: base rate ₹80/KG, target MT, conversion "1 MT = 1000 KG"
 *   → rate per MT = ₹80 × 1000 = ₹80,000
 *
 * @param {Array} conversions - Product's unit_conversions array
 * @param {string} baseUnit - The unit the rate is currently in
 * @param {number} baseRate - The rate per baseUnit
 * @param {string} targetUnit - The unit to convert to
 * @returns {number} Rate per targetUnit
 */
export function convertRate(conversions, baseUnit, baseRate, targetUnit) {
  if (!conversions || baseUnit === targetUnit) return baseRate;

  for (const c of conversions) {
    // Case 1: 1 targetUnit = factor baseUnits → rate per target = baseRate × factor
    if (c.from_unit === targetUnit && c.to_unit === baseUnit) {
      return Math.round(baseRate * c.factor * 100) / 100;
    }
    // Case 2: 1 baseUnit = factor targetUnits → rate per target = baseRate / factor
    if (c.from_unit === baseUnit && c.to_unit === targetUnit) {
      return Math.round((baseRate / c.factor) * 100) / 100;
    }
  }

  return baseRate; // No conversion found — keep original
}

/**
 * Convert quantity from one unit to base unit for stock tracking.
 *
 * Example: 2 MT → KG, conversion "1 MT = 1000 KG" → 2000 KG
 */
export function convertQtyToBase(conversions, fromUnit, qty, baseUnit) {
  if (!conversions || fromUnit === baseUnit) return qty;

  for (const c of conversions) {
    // 1 fromUnit = factor toUnit
    if (c.from_unit === fromUnit && c.to_unit === baseUnit) {
      return qty * c.factor;
    }
    // Reverse: 1 baseUnit = factor fromUnit → 1 fromUnit = 1/factor baseUnit
    if (c.from_unit === baseUnit && c.to_unit === fromUnit) {
      return qty / c.factor;
    }
  }

  return qty;
}
